export interface SequenceSnapshot {
  sequence: Uint16Array;
  locked: ReadonlySet<number>;
}

export interface HistoryEntry {
  label: string;
  before: SequenceSnapshot;
  after: SequenceSnapshot;
}

export const MAX_HISTORY = 200;

export function snapshot(sequence: Uint16Array, locked: ReadonlySet<number>): SequenceSnapshot {
  return { sequence: sequence.slice(), locked: new Set(locked) };
}

export class History {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];

  push(entry: HistoryEntry): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift();
    this.redoStack = [];
  }

  undo(): HistoryEntry | null {
    const entry = this.undoStack.pop();
    if (!entry) return null;
    this.redoStack.push(entry);
    return entry;
  }

  redo(): HistoryEntry | null {
    const entry = this.redoStack.pop();
    if (!entry) return null;
    this.undoStack.push(entry);
    return entry;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoLabel(): string {
    return this.undoStack[this.undoStack.length - 1]?.label ?? '';
  }

  get redoLabel(): string {
    return this.redoStack[this.redoStack.length - 1]?.label ?? '';
  }
}

export function stringCount(sequence: Uint16Array): number {
  return sequence.length >>> 1;
}

function checkIndex(sequence: Uint16Array, index: number): void {
  const n = stringCount(sequence);
  if (!Number.isInteger(index) || index < 0 || index >= n) {
    throw new RangeError(`string index ${index} is out of range (0..${n - 1})`);
  }
}

export function deleteString(sequence: Uint16Array, index: number): Uint16Array {
  checkIndex(sequence, index);
  const out = new Uint16Array(sequence.length - 2);
  out.set(sequence.subarray(0, index * 2), 0);
  out.set(sequence.subarray(index * 2 + 2), index * 2);
  return out;
}

export function insertString(sequence: Uint16Array, at: number, from: number, to: number): Uint16Array {
  const n = stringCount(sequence);
  if (!Number.isInteger(at) || at < 0 || at > n) throw new RangeError(`insert position ${at} is out of range (0..${n})`);
  if (from === to) throw new RangeError('a string must connect two different pins');
  const out = new Uint16Array(sequence.length + 2);
  out.set(sequence.subarray(0, at * 2), 0);
  out[at * 2] = from;
  out[at * 2 + 1] = to;
  out.set(sequence.subarray(at * 2), at * 2 + 2);
  return out;
}

export function setString(sequence: Uint16Array, index: number, from: number, to: number): Uint16Array {
  checkIndex(sequence, index);
  if (from === to) throw new RangeError('a string must connect two different pins');
  const out = sequence.slice();
  out[index * 2] = from;
  out[index * 2 + 1] = to;
  return out;
}

export function moveString(sequence: Uint16Array, from: number, to: number): Uint16Array {
  checkIndex(sequence, from);
  checkIndex(sequence, to);
  if (from === to) return sequence.slice();
  const a = sequence[from * 2];
  const b = sequence[from * 2 + 1];
  const without = deleteString(sequence, from);
  return insertString(without, to, a, b);
}

export function truncate(sequence: Uint16Array, count: number): Uint16Array {
  const n = stringCount(sequence);
  const keep = Math.max(0, Math.min(n, Math.floor(count)));
  return sequence.slice(0, keep * 2);
}

export function lockedAfterDelete(locked: ReadonlySet<number>, deleted: number): Set<number> {
  const out = new Set<number>();
  for (const i of locked) {
    if (i === deleted) continue;
    out.add(i > deleted ? i - 1 : i);
  }
  return out;
}

export function lockedAfterInsert(locked: ReadonlySet<number>, at: number): Set<number> {
  const out = new Set<number>();
  for (const i of locked) out.add(i >= at ? i + 1 : i);
  return out;
}

export function truncateKeepingLocked(sequence: Uint16Array, locked: ReadonlySet<number>, count: number): { sequence: Uint16Array; locked: Set<number> } {
  const n = stringCount(sequence);
  const keep = Math.max(0, Math.min(n, Math.floor(count)));
  const kept: number[] = [];
  const newLocked = new Set<number>();
  for (let i = 0; i < n; i++) {
    if (i < keep || locked.has(i)) {
      if (locked.has(i)) newLocked.add(kept.length >>> 1);
      kept.push(sequence[i * 2], sequence[i * 2 + 1]);
    }
  }
  return { sequence: Uint16Array.from(kept), locked: newLocked };
}

export function isContinuous(sequence: Uint16Array): boolean {
  for (let i = 2; i < sequence.length; i += 2) {
    if (sequence[i] !== sequence[i - 1]) return false;
  }
  return true;
}
